import * as client from "_graphql-types";
import { filterSortPage } from "../DataProvider";
import * as types from "./types";

type Sdk = types.Sdk;

export const INVESTMENT_BUSINESS_OBJECT_ENUM = 1;
export const FIRM_BUSINESS_OBJECT_ENUM = 2;
export const COMPANY_BUSINESS_OBJECT_ENUM = 3;
export const DEAL_BUSINESS_OBJECT_ENUM = 4;

interface DocCreateParams extends client.DocumentInput {
  file: any;
  firm: { id: number; name: string };
  investment: { id: number; name: string };
  deal: { id: number; name: string };
  company: { id: number; name: string };
  // Flat field alternatives (used by EditableDataGrid defaultRowValues)
  firmId?: number;
  investmentId?: number;
  companyId?: number;
  dealId?: number;
  accessLevel: number;
  isGallery: boolean;
  isApproved: boolean;
  notifyUsers: boolean;
  allowDuplicateFile: boolean;
}

export type DocumentCreateParams = types.CreateParams<DocCreateParams>;

function buildEntities(
  data: Partial<DocCreateParams>
): client.EntityAssociationInput[] {
  const entities: client.EntityAssociationInput[] = [];
  const num = (t: number | null | undefined) => t && Number(t);
  // Support both nested { firm: { id } } and flat { firmId } shapes
  const investmentId = num(data.investment?.id) || num(data.investmentId);
  const firmId = num(data.firm?.id) || num(data.firmId);
  const companyId = num(data.company?.id) || num(data.companyId);
  const dealId = num(data.deal?.id) || num(data.dealId);
  if (investmentId)
    entities.push({
      businessObjectEnumId: INVESTMENT_BUSINESS_OBJECT_ENUM,
      entityId: investmentId,
    });
  if (firmId)
    entities.push({
      businessObjectEnumId: FIRM_BUSINESS_OBJECT_ENUM,
      entityId: firmId,
    });
  if (companyId)
    entities.push({
      businessObjectEnumId: COMPANY_BUSINESS_OBJECT_ENUM,
      entityId: companyId,
    });
  if (dealId)
    entities.push({
      businessObjectEnumId: DEAL_BUSINESS_OBJECT_ENUM,
      entityId: dealId,
    });
  return entities;
}

export class DuplicateDocumentFileError extends Error {
  constructor(
    public readonly duplicateDocumentFiles: client.GetDocumentsListQuery["documents"]["items"]
  ) {
    super("Duplicate document file");
  }
}

async function duplicateCheck(sdk: Sdk, file: File) {
  const {
    documents: { items: duplicateDocumentFiles },
  } = await sdk.getDocumentsList({
    filter: {
      duplicates: file,
    },
    page: {
      offset: 0,
      limit: 10,
    },
    sort: [
      {
        field: client.DocumentSortEnum.Name,
        order: client.SortInput.Asc,
      },
    ],
  });

  if (duplicateDocumentFiles.length) {
    throw new DuplicateDocumentFileError(duplicateDocumentFiles);
  }
}

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

/*********************************************************************/
async function getList(
  sdk: Sdk,
  params: types.GetListParams<client.DocumentSortEnum>
) {
  const { pagination, filter, sort } = params;

  const res = await sdk.getDocumentsList(
    filterSortPage({
      filter,
      sort,
      pagination,
    })
  );
  console.log(res);

  return { data: res.documents?.items, total: res.documents?.total };
}

/*********************************************************************/
const TARGET_TO_BUSINESS_OBJECT_ENUM: Record<string, number> = {
  investmentId: INVESTMENT_BUSINESS_OBJECT_ENUM,
  firmId: FIRM_BUSINESS_OBJECT_ENUM,
  companyId: COMPANY_BUSINESS_OBJECT_ENUM,
  dealId: DEAL_BUSINESS_OBJECT_ENUM,
};

async function getManyReference(
  sdk: Sdk,
  params: types.GetManyReferenceParams<client.DocumentSortEnum>
) {
  const id = Number(params.id);
  const {
    pagination: { page, perPage },
    sort: { field, order },
    filter,
    target,
  } = params;

  const businessObjectEnumId = TARGET_TO_BUSINESS_OBJECT_ENUM[target];
  if (!businessObjectEnumId) {
    throw new Error(`Unknown document reference target: ${target}`);
  }

  const res = await sdk.getDocumentsList({
    filter: {
      ...filter,
      entities: [{ businessObjectEnumId, entityId: id }],
    },
    sort: [{ field, order }],
    page: {
      offset: (page - 1) * perPage,
      limit: perPage,
    },
  });

  return { data: res.documents?.items, total: res.documents?.total };
}

/*********************************************************************/
async function getOne(sdk: Sdk, params: types.GetOneParams) {
  const id = Number(params.id);

  const { document: data } = await sdk.getOneDocument({ id });

  return { data };
}

/*********************************************************************/
async function update(sdk: Sdk, params: types.UpdateParams<DocCreateParams>) {
  const id = Number(params.id);
  const { data } = params;

  const file = data.file?.rawFile;
  if (file && !data.allowDuplicateFile) {
    await duplicateCheck(sdk, file);
  }

  const entities = buildEntities(data);

  const input: client.UpdateDocumentMutationVariables = {
    id,
    date: data.date?.split("T")[0],
    documentTypeEnumId: data.documentTypeEnumId,
    entities,
    file,
    accessLevel: data.accessLevel,
    isGallery: data.isGallery,
    isApproved: data.isApproved,
    notifyUsers: data.notifyUsers,
    allowDuplicateFile: data.allowDuplicateFile,
  };
  await sdk.updateDocument(input);

  return await getOne(sdk, { id });
}

/*********************************************************************/
async function create(sdk: Sdk, param: types.CreateParams<DocCreateParams>) {
  const { data } = param;

  console.log("data ->", data);

  const file = data.file?.rawFile;
  if (!file) {
    throw new Error("Missing file");
  }
  if (!data.allowDuplicateFile) {
    // Check for duplicate file
    await duplicateCheck(sdk, file);
  }

  const entities = buildEntities(data);

  const { addDocument: doc } = await sdk.addDocument({
    file,
    date: types.required(param.data, "date").split("T")[0],
    documentTypeEnumId: types.required(param.data, "documentTypeEnumId"),
    entities,
    accessLevel: data.accessLevel,
    isGallery: data.isGallery,
    isApproved: data.isApproved,
    notifyUsers: data.notifyUsers,
    allowDuplicateFile: data.allowDuplicateFile,
  });

  return await getOne(sdk, { id: doc!.id });
}

/*********************************************************************/
async function _delete(sdk: Sdk, params: types.DeleteParams) {
  const id = Number(params.id);
  await sdk.deleteDocument({ id });

  return { data: { id } };
}

/*********************************************************************/
export const document = types.dataProvider({
  getList,
  getOne,
  create,
  update,
  getManyReference,
  delete: _delete,
});
